import React, { useState, useCallback, ReactNode } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import Button from '@mui/material/Button';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Dropzone, DropzoneFile } from '@lendica/components';

export interface DropOverlayProps {
    children: ReactNode;
    isActive: boolean; // Controlled from parent
    presignedUrl: string;
    bucket_name: string;
    onFileDrop?: (files: DropzoneFile[]) => void;
    onFileUpload?: (files: DropzoneFile[]) => void;
    onUploadFailed?: (error: Error) => void;
    getObjectKey?: (fileName: string) => string;
    accept?: { [key: string]: string[] };
    acceptHelper?: string;
    uploadHelper?: string;
    disabled?: boolean;
    maxFiles?: number; // Maximum number of files
    maxSize?: number; // Maximum file size in bytes (e.g., 10 * 1024 * 1024 for 10MB)
    minSize?: number; // Minimum file size in bytes
}

export const DropOverlay: React.FC<DropOverlayProps> = ({
    children,
    isActive,
    presignedUrl,
    bucket_name,
    onFileDrop,
    onFileUpload,
    onUploadFailed,
    getObjectKey,
    accept = {
        'image/png': ['.png'],
        'image/jpg': ['.jpg'],
        'image/jpeg': ['.jpeg'],
        'application/pdf': ['.pdf'],
    },
    acceptHelper = '(PDF, images)',
    uploadHelper = 'Drag and drop or click to upload invoices.',
    disabled = false,
    maxFiles,
    maxSize,
    minSize,
}) => {
    const [isDragActive, setIsDragActive] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState<DropzoneFile[]>([]);

    const handleDragActive = useCallback(
        (active: boolean) => {
            if (!disabled && isActive) {
                setIsDragActive(active);
            }
        },
        [disabled, isActive]
    );

    const handleFileDrop = useCallback(
        (files: DropzoneFile[]) => {
            console.log('Files dropped:', files);
            onFileDrop?.(files);
        },
        [onFileDrop]
    );

    const handleFileUpload = useCallback(
        (files: DropzoneFile[]) => {
            console.log('[DropOverlay] handleFileUpload called:', {
                filesCount: files.length,
                filesDetails: files.map(f => ({
                    path: f.path,
                    key: f.key,
                    hasKey: !!f.key,
                })),
            });
            setUploadedFiles(files);
            // Always forward the complete file list to parent
            onFileUpload?.(files);
        },
        [onFileUpload]
    );

    const handleUploadFailed = useCallback(
        (error: Error) => {
            console.error('Upload failed:', error);
            onUploadFailed?.(error);
        },
        [onUploadFailed]
    );

    if (disabled) {
        return <>{children}</>;
    }

    // If not active, just render children directly to avoid layout interference
    if (!isActive) {
        return <>{children}</>;
    }

    const showOverlay = isActive || isDragActive;

    return (
        <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
            {/* Page-level dropzone wrapper - only active when isActive is true */}
            <Dropzone
                multiple
                showDragActive={false} // We handle visual feedback ourselves
                value={uploadedFiles}
                onChange={handleFileUpload}
                onDragActive={handleDragActive}
                onDrop={handleFileDrop}
                onUploadFailed={handleUploadFailed}
                accept={accept}
                acceptHelper={acceptHelper}
                maxFiles={maxFiles}
                maxSize={maxSize}
                minSize={minSize}
                api={{
                    presignedUrl: presignedUrl,
                    presignedUrlParams: {
                        bucket_name: bucket_name,
                    },
                    getObjectKey: getObjectKey,
                }}
                styles={{
                    'position': 'absolute',
                    'top': 0,
                    'left': 0,
                    'right': 0,
                    'bottom': 0,
                    'width': '100%',
                    'height': '100%',
                    'backgroundColor': alpha('#ededf0', 0.5),
                    'backdropFilter': 'blur(8px)',
                    'WebkitBackdropFilter': 'blur(8px)',
                    'borderRadius': '8px',
                    'border': '1px dashed #dddddd',
                    'zIndex': 100, // Below the Add Bills button but above content
                    'cursor': 'pointer',
                    'display': 'flex',
                    'alignItems': 'center',
                    'justifyContent': 'center',
                    'transition': 'all 0.2s ease-in-out',
                    '&:hover': {
                        border: '1px dashed #aaaaaa',
                    },
                }}
                helperComponent={
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            color: '#666666',
                        }}
                    >
                        <CloudUploadIcon sx={{ fontSize: 48, color: 'inherit' }} />
                        <Typography color="inherit" variant="h5" fontWeight="bold" textAlign="center">
                            {uploadHelper}
                        </Typography>
                        <Typography color="inherit" variant="subtitle1">
                            {acceptHelper}
                        </Typography>
                        <Button
                            variant="outlined"
                            color="info"
                            size="large"
                            disableRipple
                            disableElevation
                            disableFocusRipple
                            disableTouchRipple
                            sx={{
                                'borderRadius': '6px',
                                'marginTop': '16px',
                                '&:hover': {
                                    backgroundColor: 'transparent',
                                    border: '1px solid #8c8ca1',
                                },
                            }}
                        >
                            Browse files
                        </Button>
                        {maxFiles && maxSize && (
                            <Typography color="inherit" variant="body2" sx={{ mt: 1 }}>
                                {`No more than ${maxFiles} files and ${
                                    maxSize / 1024 / 1024
                                }mb each.`}
                            </Typography>
                        )}
                    </Box>
                }
            />

            {/* Original content with subtle blur when dragging */}
            <Box
                sx={{
                    position: 'relative',
                    height: '100%',
                    width: '100%',
                    opacity: showOverlay ? 0.4 : 1,
                    filter: showOverlay ? 'blur(2px)' : 'none',
                    zIndex: 0,
                }}
            >
                {children}
            </Box>
        </Box>
    );
};
